<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-utensils"></i> Sala & Comande
          </div>
          <h1 class="sci-view-title">Ordini Fornitori & Scorte Sottosoglia</h1>
          <p class="sci-view-subtitle">Monitoraggio prodotti sotto la scorta minima di sicurezza e gestione riordini</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <button class="sci-btn sci-btn-secondary" @click="showAutoModal = true">
          <i class="fas fa-robot"></i>
          <span>Gestione Automatici</span>
        </button>
        <button class="sci-btn sci-btn-secondary" @click="refreshProducts" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <AutomaticOrderModal 
      :show="showAutoModal" 
      :allProducts="prodotti" 
      @close="showAutoModal = false"
      @refresh-products="refreshProducts"
    />

    <!-- Bento KPI Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap red">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Articoli Sottosoglia</span>
          <span class="kpi-value">{{ prodotti.length }}</span>
          <span class="kpi-sub">Richiedono riordino urgente</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-euro-sign"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Valore Totale Riordino</span>
          <span class="kpi-value">€ {{ totaleOrdineStimato.toFixed(2) }}</span>
          <span class="kpi-sub">Stima spesa cumulativa</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-weight-hanging"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Quantità Complessiva</span>
          <span class="kpi-value">{{ totaleQuantitaOrdine }} gr</span>
          <span class="kpi-sub">Volume totale merci da ordinare</span>
        </div>
      </div>
    </section>

    <!-- Controls Bar -->
    <section class="sci-card sci-controls-card">
      <div class="sci-controls-bar">
        <div class="sci-search-field">
          <i class="fas fa-search sci-search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Cerca prodotto per nome o codice..."
            class="sci-input search-input"
          />
          <button v-if="searchQuery" class="sci-clear-btn" @click="searchQuery = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="sci-info-badge">
          <i class="fas fa-info-circle"></i> Vengono mostrati solo i prodotti con giacenza &le; scorta minima
        </div>
      </div>
    </section>

    <!-- Products Table Card -->
    <section class="sci-card sci-table-card">
      <div class="table-responsive" v-if="filteredProdotti.length">
        <table class="sci-table">
          <thead>
            <tr>
              <th>Articolo / Descrizione</th>
              <th>Giacenza Attuale</th>
              <th>Q.tà da Ordinare (gr)</th>
              <th>Prezzo Unitario (€/100gr)</th>
              <th>Totale Stimato</th>
              <th class="text-end">Azione</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(prodotto, i) in filteredProdotti" :key="prodotto.code || i">
              <td>
                <div class="product-cell">
                  <span class="product-name">{{ prodotto.descrizio }}</span>
                  <span class="product-code">COD: {{ prodotto.code }}</span>
                </div>
              </td>
              <td>
                <span class="stock-badge danger">
                  <i class="fas fa-exclamation-circle"></i> {{ prodotto.quantita || 0 }} gr
                  <small class="stock-min">(min: {{ prodotto.quantitamin || 0 }})</small>
                </span>
              </td>
              <td>
                <div class="stepper-wrap">
                  <input
                    type="number"
                    min="0"
                    step="50"
                    v-model.number="prodotto.quantitaordine"
                    @change="updateQuantita"
                    class="sci-input-table"
                  />
                  <span class="input-unit">gr</span>
                </div>
              </td>
              <td>
                <div class="stepper-wrap">
                  <input
                    type="number"
                    min="0"
                    step="0.05"
                    v-model.number="prodotto.prezzo"
                    @change="() => updatePrezzo(prodotto.code, prodotto.prezzo)"
                    class="sci-input-table"
                  />
                  <span class="input-unit">€</span>
                </div>
              </td>
              <td>
                <span class="price-highlight">
                  € {{ ((prodotto.prezzo || 0) / 100 * (prodotto.quantitaordine || 0)).toFixed(2) }}
                </span>
              </td>
              <td class="text-end">
                <button
                  class="sci-btn-action success"
                  @click="() => ordina(prodotto.code, prodotto.quantitaordine, i)"
                  :disabled="!prodotto.quantitaordine || prodotto.quantitaordine <= 0"
                  title="Invia ordine a fornitore"
                >
                  <i class="fas fa-paper-plane"></i>
                  <span>Ordina</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="sci-state-feedback">
        <div class="sci-spinner"></div>
        <p class="state-title">Caricamento inventario scorte in corso...</p>
      </div>

      <!-- Empty State -->
      <div v-else class="sci-state-feedback">
        <div class="empty-icon-wrap green">
          <i class="fas fa-check-circle"></i>
        </div>
        <p class="state-title">Tutte le scorte sono a livello ottimale</p>
        <p class="state-subtitle">Nessun prodotto risulta attualmente sotto la scorta minima di sicurezza.</p>
      </div>
    </section>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import AutomaticOrderModal from '../components/AutomaticOrderModal.vue'

export default {
  name: 'Ordina',
  components: {
    AutomaticOrderModal
  },
  data() {
    return {
      prodotti: [],
      quantita: '',
      showAutoModal: false,
      loading: false,
      searchQuery: '',
      store
    }
  },
  computed: {
    totaleOrdineStimato() {
      return this.prodotti.reduce((sum, p) => {
        const itemTotal = ((p.prezzo || 0) / 100) * (p.quantitaordine || 0);
        return sum + itemTotal;
      }, 0);
    },
    totaleQuantitaOrdine() {
      return this.prodotti.reduce((sum, p) => sum + (Number(p.quantitaordine) || 0), 0);
    },
    filteredProdotti() {
      if (!this.searchQuery.trim()) return this.prodotti;
      const q = this.searchQuery.toLowerCase().trim();
      return this.prodotti.filter(p => {
        const name = (p.descrizio || '').toLowerCase();
        const code = (p.code || '').toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    async displayProduct() {
      this.loading = true;
      this.prodotti = [];
      const token = this.getToken();

      try {
        const response = await this.axios.post(this.store.path + 'displayProduct', {
          headers: { authorization: `Bearer ${token}` }
        }, {
          headers: { authorization: `Bearer ${token}` }
        });

        if (response.data && response.data.messaggio) {
          const list = [];
          response.data.messaggio.forEach(element => {
            element.prezzo = element.prezzo ? Number(element.prezzo) : 0;
            element.quantitamin = element.quantitamin ? Number(element.quantitamin) : 0;
            element.quantitaordine = element.quantitaordine ? Number(element.quantitaordine) : (element.quantitamin || 100);
            if (element.quantita <= element.quantitamin && element.stato !== "ordinato") {
              list.push(element);
            }
          });
          this.prodotti = list;
        }
      } catch (err) {
        console.error("Errore caricamento prodotti:", err);
      } finally {
        this.loading = false;
      }
    },
    refreshProducts() {
      this.displayProduct();
    },
    async ordina(code, quantitaordine, i) {
      const token = this.getToken();
      try {
        const response = await this.axios.post(this.store.path + 'ordina', {
          code: code,
          quantitaordine: quantitaordine,
          headers: { authorization: `Bearer ${token}` }
        }, {
          headers: { authorization: `Bearer ${token}` }
        });

        if (response.data && response.data.messaggio) {
          this.prodotti.splice(i, 1);
          alert("Ordine registrato con successo per l'articolo!");
        }
      } catch (err) {
        console.error("Errore invio ordine:", err);
        alert("Errore durante l'invio dell'ordine.");
      }
    },
    async updatePrezzo(code, prezzo) {
      const token = this.getToken();
      try {
        await this.axios.post(this.store.path + 'updatePrezzo', {
          code: code,
          prezzo: prezzo,
          headers: { authorization: `Bearer ${token}` }
        }, {
          headers: { authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error("Errore aggiornamento prezzo:", err);
      }
    },
    updateQuantita() {
      // Local state is already updated via v-model
    }
  },
  mounted() {
    this.displayProduct();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sci-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-secondary {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #ef4444;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.red {
  background: #fef2f2;
  color: #dc2626;
}

.kpi-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Card General */
.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

/* Controls Bar */
.sci-controls-card {
  padding: 16px 20px;
}

.sci-controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.sci-search-field {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 300px;
}

.sci-search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.search-input {
  padding-left: 36px;
  padding-right: 32px;
  width: 100%;
}

.sci-input {
  min-height: 42px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
}

.sci-input:focus {
  border-color: #0284c7;
  background: #ffffff;
}

.sci-clear-btn {
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

.sci-info-badge {
  font-size: 0.85rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Table */
.sci-table-card {
  padding: 0;
}

.table-responsive {
  overflow-x: auto;
}

.sci-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.sci-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.sci-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.95rem;
  vertical-align: middle;
}

.sci-table tbody tr:hover {
  background: #f8fafc;
}

.product-cell {
  display: flex;
  flex-direction: column;
}

.product-name {
  font-weight: 700;
  color: #0f172a;
}

.product-code {
  font-family: monospace;
  font-size: 0.8rem;
  color: #64748b;
}

.stock-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
}

.stock-badge.danger {
  background: #fee2e2;
  color: #dc2626;
}

.stock-min {
  font-weight: 500;
  color: #991b1b;
  margin-left: 4px;
}

.stepper-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sci-input-table {
  width: 90px;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
  background: #f8fafc;
  font-weight: 600;
  text-align: right;
}

.sci-input-table:focus {
  border-color: #0284c7;
  background: #ffffff;
}

.input-unit {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

.price-highlight {
  font-size: 1.05rem;
  font-weight: 800;
  color: #059669;
}

.sci-btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.sci-btn-action.success {
  background: #16a34a;
  color: #ffffff;
}

.sci-btn-action.success:hover:not(:disabled) {
  background: #15803d;
}

.sci-btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sci-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}
</style>
